import { API_ENDPOINTS, getApiUrl, getAuthHeaders } from "../config/api";
import type {
  EventPromoter,
  PromoterGuest,
  PublicRsvpMeta,
  RsvpGuest,
  RsvpGuestCreate,
  RsvpGuestUpdate,
  RsvpSettings,
  RsvpSettingsPatch,
} from "../types/rsvp";
import { sumTierCounts } from "../types/rsvp";
import {
  createEventPromoterLocal,
  deleteEventPromoterLocal,
  listEventPromotersLocal,
  mergeTemplateConfig,
  updateEventPromoterLocal,
  withResolvedTemplateSettings,
} from "../utils/ticketTemplateStore";
import {
  createRsvpGuestLocal,
  deletePromoterGuestLocal,
  deleteRsvpGuestLocal,
  getPublicRsvpMetaLocal,
  getRsvpSettingsLocal,
  listPromoterGuestsLocal,
  listRsvpGuestsLocal,
  rememberEventMetaLocal,
  submitPublicRsvpLocal,
  updatePromoterGuestLocal,
  updateRsvpGuestLocal,
  updateRsvpSettingsLocal,
} from "../utils/rsvpStore";
import { readAuthToken } from "../utils/authStorage";

function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  const msg = err instanceof Error ? err.message : String(err);
  return /Failed to fetch|NetworkError|Load failed|ECONNREFUSED|fetch failed/i.test(msg);
}

/** Use backend when reachable; fall back to localStorage only on network failure. */
async function tryApi<T>(fn: () => Promise<T>, fallback: () => T | Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (isNetworkError(err)) {
      return fallback();
    }
    throw err;
  }
}

/** Prefer backend for public links; only fall back to local when the API is unreachable. */
async function tryPublicApi<T>(fn: () => Promise<T>, fallback: () => T | Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (isNetworkError(err)) {
      return fallback();
    }
    throw err;
  }
}

function normalizeGuest(raw: RsvpGuest): RsvpGuest {
  const amountPaid = raw.amount_paid ?? raw.amount ?? null;
  const amount = raw.amount ?? (amountPaid != null ? Number(amountPaid) : null);
  return {
    ...raw,
    tier_counts: raw.tier_counts ?? {},
    amount: amount != null ? Number(amount) : null,
    amount_paid: amountPaid != null ? Number(amountPaid) : amount,
    approved: raw.approved ?? false,
    payment_status: raw.payment_status ?? "none",
  };
}

function normalizeSettings(settings: RsvpSettings): RsvpSettings {
  return withResolvedTemplateSettings({
    ...settings,
    template_config: mergeTemplateConfig(settings.template_config),
  });
}

function normalizePublicMeta(meta: PublicRsvpMeta): PublicRsvpMeta {
  return {
    ...meta,
    ticket_tiers: meta.ticket_tiers ?? [],
    template_config: mergeTemplateConfig(meta.template_config),
  };
}

function toApiGuestBody(payload: RsvpGuestCreate | RsvpGuestUpdate): Record<string, unknown> {
  const { tier_counts, amount, amount_paid, ...rest } = payload;
  const body: Record<string, unknown> = { ...rest };
  const fromTiers = sumTierCounts(tier_counts);
  // The chosen tier(s) are persisted alongside the aggregate party size — the
  // guest table holds Ticket Tier per the RSVP / Walk-in customer flows.
  if (tier_counts !== undefined) {
    body.tier_counts = tier_counts ?? {};
  }
  if (body.party_size == null && fromTiers > 0) {
    body.party_size = fromTiers;
  }
  const paid = amount_paid ?? amount;
  if (paid != null) {
    body.amount_paid = Number(paid);
  }
  // Default required create fields only when submitting a full guest row.
  if ("name" in payload && payload.name) {
    if (!body.category) {
      body.category = "stag";
    }
    if (body.party_size == null) {
      body.party_size = 1;
    }
  }
  return body;
}

/** A failed request whose 422 body named the offending fields (`{loc: [..., "phone"], msg}`). */
export class RsvpFieldError extends Error {
  fieldErrors: Record<string, string>;

  constructor(message: string, fieldErrors: Record<string, string>) {
    super(message);
    this.name = "RsvpFieldError";
    this.fieldErrors = fieldErrors;
  }
}

function parseFailure(text: string, status: number): Error {
  if (!text) return new Error(`Request failed (${status})`);
  try {
    const parsed = JSON.parse(text) as { detail?: unknown };
    if (typeof parsed.detail === "string") return new Error(parsed.detail);
    if (Array.isArray(parsed.detail)) {
      const fieldErrors: Record<string, string> = {};
      const messages: string[] = [];
      for (const item of parsed.detail as Array<{ loc?: unknown[]; msg?: string }>) {
        const msg = String(item?.msg ?? "").replace(/^Value error, /, "");
        if (!msg) continue;
        messages.push(msg);
        const field = item.loc?.[item.loc.length - 1];
        if (typeof field === "string" && !(field in fieldErrors)) fieldErrors[field] = msg;
      }
      if (messages.length) return new RsvpFieldError(messages.join(" "), fieldErrors);
    }
  } catch {
    /* use raw text */
  }
  return new Error(text);
}

async function apiRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers ?? undefined);
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    throw parseFailure(text, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function publicRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers ?? undefined);
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }
  // Intentionally no Authorization — public RSVP must not trigger auth recovery.
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    throw parseFailure(text, res.status);
  }
  return (await res.json()) as T;
}

export const rsvpApi = {
  listGuests: (eventId: string, _token?: string) =>
    tryApi(
      () =>
        apiRequest<RsvpGuest[]>(getApiUrl(API_ENDPOINTS.RSVP.GUESTS(eventId)), {
          method: "GET",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
        }).then((rows) => rows.map(normalizeGuest)),
      () => listRsvpGuestsLocal(eventId),
    ),

  searchGuests: (eventId: string, q: string, _token?: string) =>
    tryApi(
      () =>
        apiRequest<RsvpGuest[]>(getApiUrl(API_ENDPOINTS.RSVP.GUEST_SEARCH(eventId, q)), {
          method: "GET",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
        }).then((rows) => rows.map(normalizeGuest)),
      () => {
        const term = q.trim().toLowerCase();
        return listRsvpGuestsLocal(eventId)
          .filter(
            (g) =>
              g.phone.toLowerCase().includes(term) ||
              g.name.toLowerCase().includes(term) ||
              (g.email ?? "").toLowerCase().includes(term),
          )
          .slice(0, 10);
      },
    ),

  createGuest: (eventId: string, payload: RsvpGuestCreate, _token?: string) =>
    tryApi(
      () =>
        apiRequest<RsvpGuest>(getApiUrl(API_ENDPOINTS.RSVP.GUESTS(eventId)), {
          method: "POST",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
          body: JSON.stringify(toApiGuestBody(payload)),
        }).then(normalizeGuest),
      () => createRsvpGuestLocal(eventId, payload),
    ),

  updateGuest: (eventId: string, guestId: string, payload: RsvpGuestUpdate, _token?: string) =>
    tryApi(
      () =>
        apiRequest<RsvpGuest>(getApiUrl(API_ENDPOINTS.RSVP.GUEST(eventId, guestId)), {
          method: "PUT",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
          body: JSON.stringify(toApiGuestBody(payload)),
        }).then(normalizeGuest),
      () => updateRsvpGuestLocal(eventId, guestId, payload),
    ),

  checkInGuest: (eventId: string, guestId: string, _token?: string) =>
    apiRequest<RsvpGuest>(getApiUrl(API_ENDPOINTS.RSVP.GUEST_CHECK_IN(eventId, guestId)), {
      method: "POST",
      headers: getAuthHeaders(readAuthToken() ?? undefined),
    }).then(normalizeGuest),

  undoCheckInGuest: (eventId: string, guestId: string, _token?: string) =>
    apiRequest<RsvpGuest>(getApiUrl(API_ENDPOINTS.RSVP.GUEST_CHECK_IN(eventId, guestId)), {
      method: "DELETE",
      headers: getAuthHeaders(readAuthToken() ?? undefined),
    }).then(normalizeGuest),

  deleteGuest: (eventId: string, guestId: string, _token?: string) =>
    tryApi(
      () =>
        apiRequest<void>(getApiUrl(API_ENDPOINTS.RSVP.GUEST(eventId, guestId)), {
          method: "DELETE",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
        }),
      () => {
        deleteRsvpGuestLocal(eventId, guestId);
      },
    ),

  getSettings: async (eventId: string, _token?: string) => {
    const settings = await tryApi(
      () =>
        apiRequest<RsvpSettings>(getApiUrl(API_ENDPOINTS.RSVP.SETTINGS(eventId)), {
          method: "GET",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
        }),
      () => getRsvpSettingsLocal(eventId),
    );
    return normalizeSettings(settings);
  },

  updateSettings: (eventId: string, payload: RsvpSettingsPatch, _token?: string) =>
    tryApi(
      () =>
        apiRequest<RsvpSettings>(getApiUrl(API_ENDPOINTS.RSVP.SETTINGS(eventId)), {
          method: "PATCH",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
          body: JSON.stringify(payload),
        }).then(normalizeSettings),
      () => updateRsvpSettingsLocal(eventId, payload),
    ),

  rememberEventMeta: (
    eventId: string,
    meta: {
      name: string;
      event_date: string;
      city: string;
      artist?: string | null;
      ticket_tiers?: { id: string; name: string; price: number }[];
    },
  ) => {
    rememberEventMetaLocal(eventId, meta);
  },

  listPromoterGuests: (_token?: string) =>
    tryApi(
      () =>
        apiRequest<PromoterGuest[]>(getApiUrl(API_ENDPOINTS.RSVP.PROMOTER_GUESTS), {
          method: "GET",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
        }),
      () => listPromoterGuestsLocal(),
    ),

  updatePromoterGuest: (
    phone: string,
    payload: { name: string; instagram?: string | null },
    _token?: string,
  ) =>
    tryApi(
      () =>
        apiRequest<PromoterGuest>(getApiUrl(API_ENDPOINTS.RSVP.PROMOTER_GUEST(phone)), {
          method: "PATCH",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
          body: JSON.stringify(payload),
        }),
      () => {
        updatePromoterGuestLocal(phone, payload);
        const row = listPromoterGuestsLocal().find((g) => g.phone === phone.replace(/[\s\-()]/g, "").trim());
        if (!row) throw new Error("Guest not found.");
        return row;
      },
    ),

  deletePromoterGuest: (phone: string, _token?: string) =>
    tryApi(
      () =>
        apiRequest<void>(getApiUrl(API_ENDPOINTS.RSVP.PROMOTER_GUEST(phone)), {
          method: "DELETE",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
        }),
      () => {
        deletePromoterGuestLocal(phone);
      },
    ),

  publicMeta: (inviteToken: string) =>
    tryPublicApi(
      () =>
        publicRequest<PublicRsvpMeta>(getApiUrl(API_ENDPOINTS.RSVP.PUBLIC_META(inviteToken))).then(
          normalizePublicMeta,
        ),
      () => {
        const meta = getPublicRsvpMetaLocal(inviteToken);
        if (!meta) throw new Error("This RSVP link is invalid or has expired.");
        return normalizePublicMeta(meta);
      },
    ),

  publicSubmit: (inviteToken: string, payload: RsvpGuestCreate) =>
    tryPublicApi(
      () =>
        publicRequest<RsvpGuest>(getApiUrl(API_ENDPOINTS.RSVP.PUBLIC_SUBMIT(inviteToken)), {
          method: "POST",
          body: JSON.stringify(toApiGuestBody(payload)),
        }).then(normalizeGuest),
      () => submitPublicRsvpLocal(inviteToken, payload),
    ),

  listPromoters: (eventId: string, _token?: string) =>
    tryApi(
      () =>
        apiRequest<EventPromoter[]>(getApiUrl(API_ENDPOINTS.RSVP.PROMOTERS(eventId)), {
          method: "GET",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
        }),
      () => listEventPromotersLocal(eventId),
    ),

  createPromoter: (eventId: string, name: string, _token?: string) =>
    tryApi(
      () =>
        apiRequest<EventPromoter>(getApiUrl(API_ENDPOINTS.RSVP.PROMOTERS(eventId)), {
          method: "POST",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
          body: JSON.stringify({ name }),
        }),
      () => createEventPromoterLocal(eventId, name),
    ),

  updatePromoter: (eventId: string, promoterId: string, name: string, _token?: string) =>
    tryApi(
      () =>
        apiRequest<EventPromoter>(
          getApiUrl(API_ENDPOINTS.RSVP.PROMOTER(eventId, promoterId)),
          {
            method: "PATCH",
            headers: getAuthHeaders(readAuthToken() ?? undefined),
            body: JSON.stringify({ name }),
          },
        ),
      () => updateEventPromoterLocal(eventId, promoterId, name),
    ),

  deletePromoter: (eventId: string, promoterId: string, _token?: string) =>
    tryApi(
      () =>
        apiRequest<void>(getApiUrl(API_ENDPOINTS.RSVP.PROMOTER(eventId, promoterId)), {
          method: "DELETE",
          headers: getAuthHeaders(readAuthToken() ?? undefined),
        }),
      () => {
        deleteEventPromoterLocal(eventId, promoterId);
      },
    ),
};
